import styled from 'styled-components'
import { SPACING, BORDER_RADIUS, TYPOGRAPHY, FONT_WEIGHT, MEDIA } from '../../styles/theme'

export const PageContainer = styled.div`
  padding: ${SPACING.xl};
  max-width: 960px;
  margin: 0 auto;

  ${MEDIA.mobile} {
    padding: ${SPACING.lg};
  }
`

export {
  PageHeader,
  BackButton,
  HeaderInfo,
  PageTitle,
  PageSubtitle,
} from '../../styles/pageHeaderStyles'

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  flex-shrink: 0;
`

export const SectionTitle = styled.h2`
  font-size: 11px;
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  margin: 0 0 ${SPACING.sm} 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

// Full-width list of rows
export const CardsGrid = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: ${BORDER_RADIUS.lg};
  overflow: hidden;
  margin-bottom: ${SPACING.xl};
`

export const ResearchCard = styled.div<{ $accentColor: string; $clickable?: boolean }>`
  background: var(--color-node-bg, var(--color-bg-elevated));
  border-left: 3px solid ${(p) => p.$accentColor};
  display: flex;
  align-items: center;
  gap: ${SPACING.lg};
  padding: ${SPACING.md} ${SPACING.md};
  min-height: 64px;
  cursor: ${(p) => (p.$clickable === false ? 'default' : 'pointer')};
  transition: background 0.15s ease;

  &:hover {
    background: ${(p) =>
      p.$clickable === false ? 'inherit' : 'var(--color-bg-hover, rgba(0, 0, 0, 0.03))'};
  }

  & + & {
    border-top: 1px solid var(--color-border);
  }
`

export const CardIconWrapper = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${BORDER_RADIUS.md};
  background: ${(p) => p.$color}18;
  flex-shrink: 0;
`

// CardHeader now just wraps icon + info side by side
export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm};
  flex: 1;
  min-width: 0;
`

export const CardInfo = styled.div`
  flex: 1;
  min-width: 0;
`

export const CardLabel = styled.div`
  font-size: ${TYPOGRAPHY.body.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const CardParent = styled.div`
  font-size: 11px;
  color: var(--color-text-tertiary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const CardDescription = styled.div`
  font-size: 11px;
  color: var(--color-text-secondary);
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const CardStats = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.md};
  margin-top: 3px;
  flex-wrap: wrap;
`

export const StatChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: var(--color-text-tertiary);
  white-space: nowrap;
`

export const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`

export const CardActionButton = styled.button<{ $primary?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: ${BORDER_RADIUS.md};
  cursor: pointer;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  white-space: nowrap;
  transition:
    color 0.15s ease,
    background 0.15s ease,
    border-color 0.15s ease;

  ${({ $primary }) =>
    $primary
      ? `
    border: 1px solid var(--color-primary);
    background: var(--color-primary);
    color: #fff;
    &:hover {
      background: var(--color-primary-dark, #4f46e5);
      border-color: var(--color-primary-dark, #4f46e5);
    }
  `
      : `
    border: none;
    background: transparent;
    color: var(--color-text-secondary);
    &:hover {
      color: var(--color-primary);
      background: var(--color-primary-bg, rgba(99, 102, 241, 0.06));
    }
  `}
`

export const CardActionSep = styled.div`
  width: 1px;
  height: 16px;
  background: var(--color-border);
  margin: 0 2px;
`

export const DropdownWrapper = styled.div`
  display: flex;
`

export const EmptySection = styled.div`
  text-align: center;
  padding: ${SPACING.xl} ${SPACING.lg};
  color: var(--color-text-tertiary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  background: var(--color-bg-subtle, rgba(0, 0, 0, 0.02));
  border-radius: ${BORDER_RADIUS.lg};
  border: 1px dashed var(--color-border);
  margin-bottom: ${SPACING.xl};
`

// Section header + dense row primitives live in a sibling file to keep this
// one under the file-size cap. Re-exported for backwards compatibility.
export {
  SectionHeader,
  SectionHeaderTitle,
  SectionHeaderCount,
  RowContainer,
  ResearchRow,
  RowAccent,
  RowIconBadge,
  RowMain,
  RowTitleLine,
  RowTitle,
  RowTypeLabel,
  RowStatusLine,
  RowStatusChip,
  RowStatusItem,
  RowStatusSep,
  RowMetric,
  RowMetricNumber,
  RowMetricLabel,
  RowActions,
  RowActionButton,
  RowRecordDot,
  RowMoreButton,
} from './ResearchRowStyles'
